import _ from 'lodash';
import {EnrichedAstNode, AstView} from '../types';
import {findNodeById} from '../AstSearchers';
import {recalculateInnerPosition} from '../helpers';

export type AdjustmentDeltas = {top: number; right: number; bottom: number; left: number};

export type Snaps = {x: number[]; y: number[]};

export type PositionDeltas = {
    absoluteXDelta: number;
    absoluteYDelta: number;
    widthDelta: number;
    heightDelta: number;
};

/**
 * Will change all children of a node.
 * Children will only change position.
 * Relative position to parent will be preserved.
 * * @param view - View to apply changes too.
 * * @param startingNodeId - root node that deltas should be applied too
 * * @param deltas - changes
 */
export function applyPositionToNode(view: AstView, startingNodeId: string, delta: PositionDeltas, snaps: Snaps) {
    const viewCopy = _.cloneDeep(view);

    const startingNode = findNodeById(viewCopy.tree, startingNodeId);
    if (!startingNode) {
        throw new Error('can not find node.');
    }

    const snappedDeltas = applyDeltaToNodeWithSnap(startingNode, delta, snaps);

    startingNode.children.forEach((child) => {
        recursiveApplyPositionChange(child, snappedDeltas);
    });
    return viewCopy;
}

/**
 * Applies delta to nodes.
 * Doesn't allow negative widths/heighs
 */
function applyDeltaToNode(node: EnrichedAstNode, delta: PositionDeltas) {
    node.position.absoluteX += delta.absoluteXDelta;
    node.position.absoluteY += delta.absoluteYDelta;
    node.position.width = Math.max(node.position.width + delta.widthDelta, 1); // don't allow negative height/width nodes
    node.position.height = Math.max(node.position.height + delta.heightDelta, 1); // don't allow negative height/width nodes
}

const snapRes = 15;
/**
 * Applies delta to nodes.
 * Doesn't allow negative widths/heighs
 */
function applyDeltaToNodeWithSnap(node: EnrichedAstNode, delta: PositionDeltas, snaps: Snaps) {
    const originalPositions = _.clone(node.position);

    node.position.absoluteX += delta.absoluteXDelta;
    node.position.absoluteY += delta.absoluteYDelta;
    node.position.width = Math.max(node.position.width + delta.widthDelta, 1); // don't allow negative height/width nodes
    node.position.height = Math.max(node.position.height + delta.heightDelta, 1); // don't allow negative height/width nodes

    snaps.x.forEach((snap) => {
        if (Math.abs(node.position.absoluteX - snap) < snapRes) {
            node.position.absoluteX = snap;
        }
        if (Math.abs(node.position.absoluteX + node.position.width - snap) < snapRes) {
            node.position.width = snap - node.position.absoluteX;
        }
    });

    snaps.y.forEach((snap) => {
        if (Math.abs(node.position.absoluteY - snap) < snapRes) {
            node.position.absoluteY = snap;
        }
        if (Math.abs(node.position.absoluteY + node.position.height - snap) < snapRes) {
            node.position.height = snap - node.position.absoluteY;
        }
    });

    return {
        absoluteXDelta: node.position.absoluteX - originalPositions.absoluteX,
        absoluteYDelta: node.position.absoluteY - originalPositions.absoluteY,
        widthDelta: node.position.width - originalPositions.width,
        heightDelta: node.position.height - originalPositions.height,
    };
}

/**
 * This function ignore width/height changes.
 * Applies XY changes to all child nodes.
 */
function recursiveApplyPositionChange(node: EnrichedAstNode, delta: PositionDeltas) {
    applyDeltaToNode(node, {
        absoluteYDelta: delta.absoluteYDelta,
        absoluteXDelta: delta.absoluteXDelta,
        widthDelta: 0,
        heightDelta: 0,
    });
    node.children.forEach((child) => recursiveApplyPositionChange(child, delta));
}

export function adjustPadding(node: EnrichedAstNode, deltas: AdjustmentDeltas) {
    if (node.calculatedLayoutAbstraction?.widthSpacingStrategy === 'hug') {
        adjustPaddingByDeltasNodeChange(node, {left: deltas.left, right: deltas.right, top: 0, bottom: 0});
    } else {
        adjustPaddingByDeltasChildChanges(node, {left: deltas.left, right: deltas.right, top: 0, bottom: 0});
    }

    if (node.calculatedLayoutAbstraction?.heightSpacingStrategy === 'hug') {
        adjustPaddingByDeltasNodeChange(node, {left: 0, right: 0, top: deltas.top, bottom: deltas.bottom});
    } else {
        adjustPaddingByDeltasChildChanges(node, {left: 0, right: 0, top: deltas.top, bottom: deltas.bottom});
    }
}

export function adjustPaddingByDeltasNodeChange(node: EnrichedAstNode, deltas: AdjustmentDeltas) {
    // if hug. just reduce size of thing.
    node.position.absoluteX += deltas.left;
    node.position.absoluteY += deltas.top;
    node.position.width -= deltas.right + deltas.left;
    node.position.height -= deltas.bottom + deltas.top;

    node.innerPosition.paddingTop += deltas.top;
    node.innerPosition.paddingBottom += deltas.bottom;
    node.innerPosition.paddingLeft += deltas.left;
    node.innerPosition.paddingRight += deltas.right;

    recalculateInnerPosition(node);
}

export function adjustPaddingByDeltasChildChanges(node: EnrichedAstNode, deltas: AdjustmentDeltas) {
    // if hug. just reduce size of thing.

    node.innerPosition.paddingTop += deltas.top;
    node.innerPosition.paddingBottom += deltas.bottom;
    node.innerPosition.paddingLeft += deltas.left;
    node.innerPosition.paddingRight += deltas.right;

    node.children.forEach((child) => {
        const anchors = findAnchorsOfChild(node, child);
        recursiveApplyPositionChange(child, {
            absoluteXDelta: anchors.left * deltas.left + anchors.right * deltas.right,
            absoluteYDelta: anchors.top * deltas.top + anchors.bottom * deltas.bottom,
            widthDelta: 0,
            heightDelta: 0,
        });
    });
}

export function findAnchorsOfChild(parent: EnrichedAstNode, child: EnrichedAstNode) {
    // child flex direction
    // child align.
    const flexDirection = parent.calculatedLayoutAbstraction?.flexDirection;

    // TODO space between special case
    const {alignTopLeft, alignBottomRight} = getMultipleFromAlign(child.calculatedLayoutAbstraction?.alignSelf);
    const {justifyTopLeft, justifyBottomRight} = getMultipleFromJustify(
        parent.calculatedLayoutAbstraction?.justifyContent
    );

    if (flexDirection === 'column') {
        return {
            left: alignTopLeft,
            right: alignBottomRight,
            top: justifyTopLeft,
            bottom: justifyBottomRight,
        };
    } else {
        return {
            top: alignTopLeft,
            bottom: alignBottomRight,
            left: justifyTopLeft,
            right: justifyBottomRight,
        };
    }
}

const getMultipleFromAlign = (align: 'flex-start' | 'flex-end' | 'center' | 'stretch' | undefined) => {
    if (align === 'center') {
        return {
            alignTopLeft: 0.5,
            alignBottomRight: 0.5,
        };
    } else if (align === 'flex-start') {
        return {
            alignTopLeft: 1,
            alignBottomRight: 0,
        };
    } else if (align === 'flex-end') {
        return {
            alignTopLeft: 0,
            alignBottomRight: 1,
        };
    } else {
        return {
            alignTopLeft: 1,
            alignBottomRight: 1,
        };
    }
};

const getMultipleFromJustify = (justify: 'flex-start' | 'flex-end' | 'center' | 'space-between' | undefined) => {
    if (justify === 'center') {
        return {
            justifyTopLeft: 0.5,
            justifyBottomRight: 0.5,
        };
    } else if (justify === 'flex-start') {
        return {
            justifyTopLeft: 1,
            justifyBottomRight: 0,
        };
    } else if (justify === 'flex-end') {
        return {
            justifyTopLeft: 0,
            justifyBottomRight: 1,
        };
    } else if (justify === 'space-between') {
        return {
            justifyTopLeft: 0,
            justifyBottomRight: 0,
        };
    } else {
        return {
            justifyTopLeft: 1,
            justifyBottomRight: 1,
        };
    }
};
